import { setBrandTitle } from "@/lib/page-title";
import { useBrandImage, useStoredBrand } from "./brand-cache";
import { useEffect, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBranding, brandingQueryKey } from "@/api";
import { usePreferencesStore, DARK_FAMILY, THEMES, type Theme } from "@/preferences-store";

export interface VisualBranding {
  logo?: string | null;
  colors?: Record<string, unknown>;
  images?: Record<string, unknown>;
  texts?: Record<string, unknown>;
  settings?: { displayName?: string; defaultTheme?: Theme; darkModeDefault?: boolean };
}
export function organizationTheme(settings?: VisualBranding["settings"]): Theme | null {
  if (settings?.defaultTheme && THEMES.includes(settings.defaultTheme))
    return settings.defaultTheme;
  if (typeof settings?.darkModeDefault === "boolean")
    return settings.darkModeDefault ? "dark" : "light";
  return null;
}
export function hexColor(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(value)) return;
  return value.length === 4
    ? `#${value
        .slice(1)
        .split("")
        .map((v) => v + v)
        .join("")}`
    : value;
}
function channels(hex: string): number[] {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
}
function luminance(hex: string): number {
  const values = channels(hex)
    .map((v) => v / 255)
    .map((v) => (v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return values[0]! * 0.2126 + values[1]! * 0.7152 + values[2]! * 0.0722;
}
export function contrast(a: string, b: string): number {
  const x = luminance(a),
    y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}
function foreground(color: string): string {
  return contrast(color, "#ffffff") >= contrast(color, "#000000") ? "#ffffff" : "#000000";
}
// Ajusta somente texto e foco; superfícies preservam a cor cadastrada da marca.
function readablePrimary(color: string, dark: boolean): string {
  const background = dark ? "#18181b" : "#f3f4f6";
  const rgb = channels(color);
  let next = color;
  for (let step = 1; contrast(next, background) < 4.5 && step <= 100; step++) {
    next = `#${rgb
      .map((v) =>
        Math.round(v + (((dark ? 255 : 0) - v) * step) / 100)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")}`;
  }
  return next;
}
export function brandThemeStyle(colors: Record<string, unknown> = {}, dark = false): CSSProperties {
  const style: Record<string, string> = {};
  const primary = hexColor(colors.primary);
  const accent = hexColor(colors.accent);
  if (primary) {
    const readable = readablePrimary(primary, dark);
    style["--primary"] = style["--sidebar-primary"] = primary;
    for (const key of ["--primary-text", "--ring", "--sidebar-ring"]) style[key] = readable;
    style["--primary-foreground"] = style["--sidebar-primary-foreground"] = foreground(primary);
  }
  if (accent) {
    style["--accent"] = style["--sidebar-accent"] = accent;
    style["--accent-foreground"] = style["--sidebar-accent-foreground"] = foreground(accent);
  }
  return style as CSSProperties;
}
export function brandLogo(data: VisualBranding | undefined, dark: boolean): string | null {
  const candidate =
    data?.images?.[dark ? "logoDark" : "logoLight"] || data?.logo || data?.images?.logo;
  return typeof candidate === "string" && candidate.trim() ? candidate : null;
}
export function useBrandLogo(data: VisualBranding | undefined): string | null {
  const theme = usePreferencesStore((s) => s.theme);
  return useBrandImage(brandLogo(data, DARK_FAMILY.has(theme)));
}
export function useBrandingIdentity(data: VisualBranding | undefined): void {
  const setOrganizationTheme = usePreferencesStore((s) => s.setOrganizationTheme);
  const defaultTheme = organizationTheme(data?.settings);
  useEffect(() => {
    setOrganizationTheme(defaultTheme);
  }, [defaultTheme, setOrganizationTheme]);
  const theme = usePreferencesStore((s) => s.theme);
  useEffect(() => {
    const root = document.documentElement;
    const style = brandThemeStyle(data?.colors, DARK_FAMILY.has(theme));
    const previous = Object.keys(style).map((key) => [key, root.style.getPropertyValue(key)]);
    for (const [key, value] of Object.entries(style)) root.style.setProperty(key, String(value));
    const tabTitle = data?.texts?.tabTitle;
    setBrandTitle(
      (typeof tabTitle === "string" && tabTitle.trim()) ||
        data?.settings?.displayName?.trim() ||
        "Education",
    );
    // Substitui todos os favicons: o navegador pode preferir o PNG ao SVG.
    const icons = [...document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')];
    const favicon = data?.images?.favicon;
    let custom: HTMLLinkElement | undefined;
    if (typeof favicon === "string" && favicon) {
      for (const icon of icons) icon.remove();
      custom = document.createElement("link");
      custom.rel = "icon";
      custom.href = favicon;
      document.head.appendChild(custom);
    }
    return () => {
      document.title = "Education";
      for (const [key, value] of previous) {
        if (value) root.style.setProperty(key!, value);
        else root.style.removeProperty(key!);
      }
      custom?.remove();
      if (custom) for (const icon of icons) document.head.appendChild(icon);
    };
  }, [data, theme]);
}
export function useApplyBranding(): void {
  const { data } = useQuery({ queryKey: brandingQueryKey, queryFn: getBranding });
  const cached = useStoredBrand();
  useBrandingIdentity(data ?? cached);
}
